// Identifies API requests originating from the Astro SSR Node process.
//
// SSR requests egress from a handful of shared ECS NAT gateway IPs with a
// generic `node`/undici user-agent, which makes them nearly impossible to tell
// apart from other traffic in Cloudflare / API access logs — identifying the
// source of a rate-limit spike previously meant reverse-engineering UA + JA3 +
// source IP across teams. Tagging every SSR request with an explicit service
// identity (and the running commit) turns that into a single log filter, and
// lets the API team rate-limit or allow-list SSR traffic deliberately.
//
// The value matches the `astro-ssr` service/source tag already used by the SSR
// Datadog logger, so request logs and application logs line up.
export const HR_SOURCE_HEADER = 'X-HR-Source';
export const HR_SOURCE_SSR = 'astro-ssr';
export const HR_COMMIT_SHA_HEADER = 'X-HR-Commit-Sha';

// The rest adapter runs in both the browser and the SSR Node process; `window`
// is the reliable discriminator (mirrors the `X-Requested-From` branch).
function isServer(): boolean {
  return typeof window === 'undefined';
}

/**
 * Identifying headers for server-side (SSR) API requests. Returns an empty
 * object in the browser: these headers must never be sent from the browser,
 * where they are redundant and would trigger a CORS preflight on cross-origin
 * API calls. SSR (Node) `fetch` has no CORS/preflight, so it is safe there.
 */
export function getSsrRequestHeaders(): Record<string, string> {
  if (!isServer()) {
    return {};
  }

  const headers: Record<string, string> = {
    [HR_SOURCE_HEADER]: HR_SOURCE_SSR,
  };

  // `COMMIT_SHA` is a runtime env var on the astro-ssr container (also read by
  // the o11y server logger + Sentry). Guard `process` so the reference is safe
  // even if this module is ever reached from a browser bundle.
  const commitSha =
    typeof process !== 'undefined' ? process.env.COMMIT_SHA : undefined;
  if (commitSha) {
    headers[HR_COMMIT_SHA_HEADER] = commitSha;
  }

  return headers;
}

/**
 * Mutating variant for callers that build a `Headers` instance (the fetch
 * adapter). No-ops in the browser.
 */
export function applySsrRequestHeaders(headers: Headers): void {
  for (const [name, value] of Object.entries(getSsrRequestHeaders())) {
    headers.set(name, value);
  }
}
